import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo, Mark, Container, VideoSlot, ScreenshotGallery } from '../components/marketing/Primitives';
import { useVideo, useImageList } from '../cms/ContentProvider';
import {
  loopSpecimen,
  problems,
  applications,
  threeSentences,
  framework,
  ledger,
  failureLab,
  scenario,
  facultyAngle,
  screenshots,
  video,
  faq,
} from '../data/medschoolData';

/* The interactive specimen: the interpret-then-compare loop, lived.
   Commit a timestamped read before the AI speaks, reconcile the AI's draft
   against the chart, and watch the append-only ledger record every scorable
   moment. */
function LoopSpecimen() {
  const [commit, setCommit] = useState(null);
  const [decision, setDecision] = useState(null);
  const s = loopSpecimen;

  const events = [];
  if (commit) {
    events.push({
      tag: 'Loop integrity \u00b7 Embedded',
      text: 'Commit recorded at 06:41, before any AI was consulted. Timestamp is part of the evidence.',
      good: true,
    });
  }
  if (decision === 'accepted') {
    events.push({
      tag: 'Trust calibration \u00b7 Affective',
      text: 'Unverified AI output entered the note against a contradicting chart. Flagged for coaching, not for punishment: this is exactly the moment the loop exists to catch.',
      good: false,
    });
  }
  if (decision === 'caught') {
    events.push(
      {
        tag: 'AI failure recognition \u00b7 Affective',
        text: 'The draft\u2019s "renal function remains stable" claim was recognized as unsupported by the chart.',
        good: true,
      },
      {
        tag: 'Reconciliation depth \u00b7 Psychomotor',
        text: 'Item-level decision with written rationale recorded.',
        good: true,
      }
    );
  }

  return (
    <div className="specimen-card !rotate-0">
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-[11px] font-semibold tracking-wide text-slate">{s.header}</p>
        <p className="text-[11px] text-faint">Interpret, then compare</p>
      </div>

      {/* The chart */}
      <p className="mt-2 text-[13px] font-semibold text-bright">{s.chart.patient}</p>
      <p className="mt-1 rounded border border-hairline bg-ink-deep/60 p-3 text-[12px] leading-relaxed text-slate">
        {s.chart.data}
      </p>

      {/* Step 1: commit before AI */}
      {!commit && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold text-slate">{s.commitPrompt}</p>
          <div className="mt-2 space-y-2">
            {s.commitOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setCommit(opt)}
                className="w-full rounded border border-hairline px-3 py-2 text-left text-[12px] leading-snug text-slate transition-colors hover:border-seal/50 hover:text-bright"
              >
                {opt.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: only now does the AI speak */}
      {commit && (
        <div className="mt-4">
          <p className="text-[11px] text-faint">
            Your read, committed: <span className="text-slate">{commit.text.toLowerCase()}</span>
          </p>
          <p className="mt-3 rounded border border-seal/30 bg-seal/5 p-3 text-[12px] leading-relaxed text-slate">
            {s.aiDraft}
          </p>
          {!decision && (
            <div className="mt-3">
              <p className="text-[11px] font-semibold text-slate">{s.reconcilePrompt}</p>
              <div className="mt-2 space-y-2">
                {s.reconcileOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setDecision(opt.outcome)}
                    className="w-full rounded border border-hairline px-3 py-2 text-left text-[12px] leading-snug text-slate transition-colors hover:border-seal/50 hover:text-bright"
                  >
                    <span className="mr-2 font-semibold text-bright">{opt.label}:</span>
                    {opt.text}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: the ledger */}
      {decision && (
        <div className="mt-4 rounded border border-hairline-dark bg-ink-deep/80 p-4">
          <p className="text-[11px] font-semibold text-bright">Assessment events written</p>
          <p className="mt-1 text-[10px] text-faint">{s.rationales[decision]}</p>
          <ul className="mt-3 space-y-2.5">
            {events.map((e) => (
              <li key={e.tag} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${e.good ? 'bg-seal' : 'bg-rose-400/80'}`}
                />
                <div>
                  <p className="text-[11px] font-semibold text-slate">{e.tag}</p>
                  <p className="text-[10px] leading-snug text-faint">{e.text}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-3 border-t border-hairline-dark pt-2.5 text-[10px] leading-snug text-faint">
            These events are append-only: they can never be edited. The student’s portfolio and the cohort’s
            accreditation map just rebuilt themselves from them. Derived, not typed.
          </p>
          <button
            type="button"
            onClick={() => {
              setCommit(null);
              setDecision(null);
            }}
            className="mt-3 text-[12px] font-medium text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
          >
            Run the loop again
          </button>
        </div>
      )}
    </div>
  );
}

export default function MedSchoolPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const preroundsVideo = useVideo('medschool.prerounds', video.embed ? { embed: video.embed } : null);
  const shots = useImageList(
    screenshots.map((s, i) => ({
      ...s,
      key: ['medschool.prerounds', 'medschool.dashboard', 'medschool.aiUse'][i],
    }))
  );

  return (
    <>
      <Seo
        title="EdAI MedSchool"
        description="Teach, assess, and document how students work with clinical knowledge and clinical AI: the interpret-then-compare loop, a concrete competency instrument on the entrustment scale, an append-only evidence ledger, and an accreditation dashboard derived from live data."
      />

      {/* Hero: claim left, the loop right. */}
      <section className="overflow-hidden bg-ink text-paper-dark-text">
        <Container className="grid items-start gap-14 py-20 md:py-28 lg:grid-cols-[1fr_1.1fr] lg:gap-12">
          <div className="lg:pt-6">
            <Mark dark>EdAI MedSchool · For medical schools</Mark>
            <h1 className="hero-rise mt-4 font-serif text-4xl leading-[1.06] md:text-5xl">
              The content-driven architecture, extended into medical education.
            </h1>
            <p className="hero-rise-2 mt-6 max-w-measure text-lg leading-relaxed text-slate-dark-text">
              EdAI MedSchool brings authority-derived content into undergraduate medical education, with PreRounds,
              a clinical AI safety training platform, as its spine. It teaches students and physicians to use clinical
              AI without being used by it, and produces the evidence trail a school needs to show an accreditor it did so.
              Students learn against the same validated knowledge they will eventually be licensed and certified on.
            </p>
            <p className="hero-rise-2 mt-4 max-w-measure text-sm leading-relaxed text-faint">
              On the right: the loop your students will live. Commit your read first. Then see what the AI drafted.
            </p>
            <div className="hero-rise-3 mt-9 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary-on-dark">Bring it to your school</Link>
              <a href="#framework" className="btn-secondary-on-dark">The competency instrument</a>
            </div>
          </div>
          <div className="hero-rise-3">
            <LoopSpecimen />
          </div>
        </Container>
      </section>

      {/* The problems */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>The problem, plainly</Mark>
          <h2 className="mt-3 max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            Your students already use AI on the wards. In a browser tab you cannot see.
          </h2>
          <div className="mt-12 grid gap-10 md:grid-cols-3 md:gap-0 md:divide-x md:divide-hairline">
            {problems.map((p) => (
              <article key={p.title} className="md:px-8 md:first:pl-0 md:last:pr-0">
                <h3 className="font-serif text-xl text-bright">{p.title}</h3>
                <p className="mt-4 leading-relaxed text-slate">{p.text}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* Three applications, one record */}
      <section className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>The product</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Three applications. One identity. One ledger.
          </h2>
          <div className="mt-12 grid gap-px overflow-hidden rounded-media border border-hairline bg-hairline md:grid-cols-3">
            {applications.map((a) => (
              <div key={a.name} className="flex flex-col bg-surface p-7">
                <h3 className="font-serif text-xl text-bright">{a.name}</h3>
                <p className="mt-1 text-sm font-medium text-seal">{a.cadence}</p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">{a.text}</p>
                {a.name === 'Practicums' && (
                  <p className="mt-4">
                    <Link to="/products/practicums" className="link-quiet text-sm font-semibold text-bright">
                      Explore Practicums
                    </Link>
                  </p>
                )}
              </div>
            ))}
          </div>
          <ol className="mt-10 space-y-2">
            {threeSentences.map((line, i) => (
              <li key={line} className="flex gap-3 leading-relaxed text-bright">
                <span aria-hidden="true" className="font-serif text-seal">{i + 1}.</span>
                <span className="font-medium">{line}</span>
              </li>
            ))}
          </ol>
        </Container>
      </section>

      {/* The framework */}
      <section id="framework" className="py-20 md:py-28">
        <Container>
          <Mark>The competency instrument</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Entrustment, extended to clinical AI.
          </h2>
          <p className="mt-5 max-w-measure-wide leading-relaxed text-slate">{framework.intro}</p>
          <div className="mt-12 grid gap-px overflow-hidden rounded-media border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {framework.elements.map((el) => (
              <div key={el.key} className="bg-surface p-6">
                <p className="font-serif text-3xl text-seal">{el.key}</p>
                <h3 className="mt-1 font-semibold text-bright">{el.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">{el.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-measure-wide text-sm leading-relaxed text-faint">{framework.milestonesNote}</p>
        </Container>
      </section>

      {/* The ledger and accreditation */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-28">
        <Container>
          <Mark dark>The evidence trail</Mark>
          <h2 className="mt-3 max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            "Show us how you taught this" should take minutes, not months.
          </h2>
          <div className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-2">
            {ledger.map((l) => (
              <div key={l.title} className="border-t border-hairline-dark pt-5">
                <h3 className="font-serif text-lg text-bright">{l.title}</h3>
                <p className="mt-2 leading-relaxed text-slate-dark-text">{l.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Failure Lab + faculty angle */}
      <section className="py-20 md:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
              <Mark>{failureLab.heading}</Mark>
              <h2 className="mt-3 max-w-[20ch] font-serif text-3xl leading-tight md:text-4xl">
                Recognizing failure is an examinable skill.
              </h2>
              <p className="mt-5 leading-relaxed text-slate">{failureLab.body}</p>
              <div className="mt-8 rounded-media border border-hairline bg-surface p-6">
                <h3 className="font-serif text-xl text-bright">{scenario.heading}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">{scenario.text}</p>
              </div>
            </div>
            <div>
              <Mark>Adoption</Mark>
              <h2 className="mt-3 max-w-[20ch] font-serif text-3xl leading-tight md:text-4xl">
                {facultyAngle.heading}.
              </h2>
              <p className="mt-5 leading-relaxed text-slate">{facultyAngle.body}</p>
            </div>
          </div>
        </Container>
      </section>

      {/* See it: video + screenshots */}
      <section className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>See it</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">{video.title}</h2>
          <p className="mt-4 max-w-measure leading-relaxed text-slate">{video.text}</p>
          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <VideoSlot
              video={preroundsVideo}
              title={video.title}
              text="A PreRounds walkthrough film is planned; request a live demo in the meantime."
            />
            <ScreenshotGallery screenshots={shots} />
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>Questions schools ask</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Asked and answered.</h2>
          <div className="mt-10 max-w-measure-wide divide-y divide-hairline border-y border-hairline">
            {faq.map((item, i) => (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-baseline justify-between gap-6 py-5 text-left"
                >
                  <span className="font-semibold text-bright">{item.q}</span>
                  <span aria-hidden="true" className="text-seal">{openFaq === i ? '\u2212' : '+'}</span>
                </button>
                {openFaq === i && <p className="pb-6 leading-relaxed text-slate">{item.a}</p>}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-hairline bg-ink-deep/40 py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            They will use AI on the wards either way. Teach it, assess it, and be able to prove you did.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">Bring it to your school</Link>
            <Link to="/products/practicums" className="btn-secondary">See the simulation lab</Link>
          </div>
        </Container>
      </section>
    </>
  );
}
